import {
  formatDate,
  formatRelative,
  INVITATION_RESEND_SECONDS,
  orgUserListSchema,
  orgUserSchema,
  USER_STATUS_LABELS,
  USER_STATUSES,
  userFormOptionsSchema,
  type InviteUserInput,
  type OrgUser,
  type UserAccessInput,
  type UserStatusName,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Search,
  Send,
  UserPlus,
  UserRoundX,
  UsersRound,
  ShieldCheck,
  ShieldOff,
  type LucideIcon,
} from 'lucide-react';
import { Fragment, useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { UserForm, userOptionsKey } from './UserForm';

const PAGE_SIZE = 20;

type Pending =
  | { kind: 'deactivate'; user: OrgUser }
  | { kind: 'cancel'; user: OrgUser }
  | { kind: 'reset2fa'; user: OrgUser }
  | null;

/** Situação do usuário, incluindo convite vencido. */
function StatusTag({ user }: { user: OrgUser }) {
  if (user.status === 'INVITED') {
    const expired = user.invitation?.expired;
    return (
      <Badge variant="outline" className={cn(expired && 'border-status-red-solid/50 text-status-red-fg')}>
        {expired ? 'Convite expirado' : USER_STATUS_LABELS.INVITED}
      </Badge>
    );
  }
  return (
    <Badge variant={user.status === 'ACTIVE' ? 'brand' : 'neutral'}>{USER_STATUS_LABELS[user.status]}</Badge>
  );
}

/** Convidado há minutos e ainda sem link: o envio falhou (o job desistiu) e é preciso reenviar. */
const inviteStuck = (user: OrgUser, now = Date.now()) =>
  !user.invitation && now - new Date(user.createdAt).getTime() > 3 * 60_000;

/** Pode reenviar já? O app espera o intervalo desde o último envio (o servidor confere de novo). */
const canResendNow = (user: OrgUser, now = Date.now()) =>
  !user.invitation || now - new Date(user.invitation.sentAt).getTime() >= INVITATION_RESEND_SECONDS * 1000;

/**
 * Usuários de uma organização (T-026c, RF-07/RF-08): lista com busca, filtros e paginação, convite
 * por e-mail, edição de acesso, desativar/reativar e reenviar/cancelar convite. Serve às empresas
 * clientes e à equipe Fluxus (`team`).
 */
export function UsersPanel({ organizationId, team = false }: { organizationId: string; team?: boolean }) {
  const { client } = useAuth();
  const can = useCan();
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [roleKey, setRoleKey] = useState('');
  const [status, setStatus] = useState<UserStatusName | ''>('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<OrgUser | 'new' | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const fieldIds = { search: useId(), role: useId(), status: useId() };

  useEffect(() => {
    const next = search.trim();
    if (next === q) return;
    const timer = setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, q]);

  const options = useQuery({
    queryKey: userOptionsKey(organizationId),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/users/options`, {
        schema: userFormOptionsSchema,
        signal,
      }),
  });

  const query = useQuery({
    queryKey: ['users', organizationId, 'list', { q, roleKey, status, page }],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/users`, {
        query: {
          q: q || undefined,
          roleKey: roleKey || undefined,
          status: status || undefined,
          page,
          pageSize: PAGE_SIZE,
        },
        schema: orgUserListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users', organizationId, 'list'] });
  const data = query.data;
  useEffect(() => {
    if (data && data.totalPages > 0 && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const userUrl = (u: OrgUser) => `/organizations/${organizationId}/users/${u.id}`;

  const action = useMutation({
    meta: { handlesError: true },
    mutationFn: async ({
      kind,
      user,
    }: {
      kind: 'deactivate' | 'reactivate' | 'resend' | 'cancel' | 'reset2fa';
      user: OrgUser;
    }) => {
      if (kind === 'resend') {
        await client.request(`${userUrl(user)}/invitation/resend`, { method: 'POST' });
      } else if (kind === 'cancel') {
        await client.request(`${userUrl(user)}/invitation`, { method: 'DELETE' });
      } else if (kind === 'reset2fa') {
        await client.request(`${userUrl(user)}/two-factor/reset`, { method: 'POST', schema: orgUserSchema });
      } else {
        await client.request(`${userUrl(user)}/${kind}`, { method: 'POST', schema: orgUserSchema });
      }
    },
    onSuccess: async (_data, { kind, user }) => {
      setPending(null);
      const messages = {
        deactivate: [
          `${user.name} foi desativado`,
          'As sessões abertas foram encerradas em todos os aparelhos.',
        ],
        reactivate: [`${user.name} foi reativado`, 'O acesso volta a valer com a mesma senha.'],
        resend: [
          'Convite reenviado',
          `Um novo link foi enviado para ${user.email}. Os anteriores deixam de valer.`,
        ],
        cancel: ['Convite cancelado', `O link enviado para ${user.email} deixou de valer.`],
        reset2fa: [
          'Verificação em duas etapas redefinida',
          `${user.name} vai cadastrar o app autenticador de novo (ou entrar só com a senha, se o perfil não exigir).`,
        ],
      } as const;
      const [title, description] = messages[kind];
      toast.success(title, { description });
      await refresh();
    },
    onError: (err) => {
      setPending(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });

  async function save(input: InviteUserInput | UserAccessInput) {
    if (editing === 'new') {
      const created = await client.request(`/organizations/${organizationId}/users`, {
        method: 'POST',
        body: input,
        schema: orgUserSchema,
      });
      toast.success('Convite enviado', {
        description: `${created.name} vai receber em ${created.email} o link para criar a senha.`,
      });
    } else if (editing) {
      await client.request(userUrl(editing), { method: 'PATCH', body: input, schema: orgUserSchema });
      toast.success('Acesso salvo');
    }
    setEditing(null);
    await refresh();
  }

  const filtered = Boolean(q || roleKey || status);
  const canCreate = can('administracao', 'create');
  const canEdit = can('administracao', 'edit');
  const canDelete = can('administracao', 'delete');
  const noun = team ? 'pessoa da equipe' : 'pessoa';

  return (
    <section aria-label={team ? 'Equipe Fluxus' : 'Usuários'} className="mt-6">
      {/* Busca, filtros e o botão numa linha só a partir do desktop: no tablet, com o menu lateral, não cabem. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="relative lg:flex-1">
          <label htmlFor={fieldIds.search} className="sr-only">
            Buscar por nome ou e-mail
          </label>
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id={fieldIds.search}
            type="search"
            placeholder="Buscar por nome ou e-mail"
            value={search}
            maxLength={100}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 pl-9"
          />
        </div>
        <div className="grid grid-cols-2 gap-3 md:flex">
          <div className="md:w-48">
            <label htmlFor={fieldIds.role} className="sr-only">
              Filtrar por perfil
            </label>
            <NativeSelect
              id={fieldIds.role}
              value={roleKey}
              onChange={(e) => {
                setRoleKey(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos os perfis</option>
              {options.data?.roles.map((r) => (
                <option key={r.id} value={r.key}>
                  {r.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="md:w-48">
            <label htmlFor={fieldIds.status} className="sr-only">
              Filtrar por situação
            </label>
            <NativeSelect
              id={fieldIds.status}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as UserStatusName | '');
                setPage(1);
              }}
            >
              <option value="">Todas as situações</option>
              {USER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {USER_STATUS_LABELS[s]}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>
        {canCreate ? (
          <Button className="h-11" onClick={() => setEditing('new')}>
            <UserPlus aria-hidden />
            Convidar {noun}
          </Button>
        ) : null}
      </div>

      <div className="mt-5">
        {query.isPending ? (
          <LoadingState rows={5} label="Carregando usuários" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
        ) : data && data.total === 0 ? (
          <EmptyState
            className="rounded-lg border border-dashed border-border py-10"
            icon={UsersRound}
            title={
              filtered ? 'Ninguém encontrado' : team ? 'Ninguém na equipe ainda' : 'Nenhum usuário ainda'
            }
            description={
              filtered
                ? 'Nenhuma pessoa corresponde à busca e aos filtros escolhidos.'
                : team
                  ? 'Convide administradores e consultores da Fluxus. Cada pessoa recebe um link por e-mail para criar a senha.'
                  : 'Convide as pessoas da empresa com o perfil e as áreas de cada uma. O convite chega por e-mail com o link para criar a senha.'
            }
            action={
              filtered ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('');
                    setRoleKey('');
                    setStatus('');
                  }}
                >
                  Limpar filtros
                </Button>
              ) : canCreate ? (
                <Button onClick={() => setEditing('new')}>
                  <UserPlus aria-hidden />
                  Convidar {noun}
                </Button>
              ) : null
            }
          />
        ) : data ? (
          <>
            <p className="mb-2 text-sm text-muted-foreground" aria-live="polite">
              {data.total === 1 ? '1 pessoa' : `${data.total} pessoas`}
            </p>
            <ul
              aria-label={team ? 'Equipe' : 'Usuários'}
              aria-busy={query.isFetching}
              className="divide-y divide-border rounded-lg border border-border bg-surface"
            >
              {data.items.map((u) => {
                const areas = [...u.departments, ...u.businessUnits].map((x) => x.name);
                const isMe = u.id === me.id;
                return (
                  <li key={u.id} className="flex items-start gap-3 px-4 py-3.5 sm:items-center">
                    <div className="grid min-w-0 flex-1 gap-x-4 gap-y-1.5 sm:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1fr)] sm:items-center">
                      <div className="min-w-0">
                        <p className="truncate font-semibold">
                          {u.name}
                          {isMe ? (
                            <span className="ml-1.5 text-xs font-normal text-muted-foreground">(você)</span>
                          ) : null}
                        </p>
                        <p className="truncate text-sm text-muted-foreground">{u.email}</p>
                      </div>
                      <div className="min-w-0 text-sm">
                        <p className="font-medium">{u.role.name}</p>
                        {team ? null : (
                          <p className="truncate text-muted-foreground" title={areas.join(', ') || undefined}>
                            {areas.length ? areas.join(', ') : 'Sem área ou unidade'}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <StatusTag user={u} />
                        {u.twoFactorEnabled ? (
                          <Badge variant="outline" title="Verificação em duas etapas ativa">
                            <ShieldCheck aria-hidden />
                            2FA
                          </Badge>
                        ) : null}
                        <span>
                          {u.status === 'INVITED'
                            ? u.invitation
                              ? `${u.invitation.expired ? 'Venceu' : 'Vale até'} ${formatDate(u.invitation.expiresAt)}`
                              : inviteStuck(u)
                                ? 'Convite não enviado — use Reenviar'
                                : 'Enviando convite…'
                            : u.lastLoginAt
                              ? `Último acesso ${formatRelative(u.lastLoginAt)}`
                              : 'Nunca entrou'}
                        </span>
                      </div>
                    </div>
                    <RowActions
                      user={u}
                      isMe={isMe}
                      actorIsAdmin={me.role.key === 'FLUXUS_ADMIN'}
                      canCreate={canCreate}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      disabled={action.isPending}
                      onEdit={() => setEditing(u)}
                      onResend={() => action.mutate({ kind: 'resend', user: u })}
                      onCancel={() => setPending({ kind: 'cancel', user: u })}
                      onDeactivate={() => setPending({ kind: 'deactivate', user: u })}
                      onReactivate={() => action.mutate({ kind: 'reactivate', user: u })}
                      onResetTwoFactor={() => setPending({ kind: 'reset2fa', user: u })}
                    />
                  </li>
                );
              })}
            </ul>
            {data.totalPages > 1 ? (
              <nav
                aria-label="Paginação de usuários"
                className="mt-3 flex items-center justify-between text-sm"
              >
                <span className="text-muted-foreground">
                  Página {data.page} de {data.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </nav>
            ) : null}
          </>
        ) : null}
      </div>

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? `Convidar ${noun}` : 'Editar acesso'}
        description={
          editing === 'new'
            ? 'A pessoa recebe um e-mail com o link para criar a senha. O link vale por tempo limitado e só pode ser usado uma vez.'
            : undefined
        }
      >
        {editing !== null ? (
          <UserForm
            key={editing === 'new' ? 'new' : editing.id}
            organizationId={organizationId}
            initial={editing === 'new' ? undefined : editing}
            team={team}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={pending?.kind === 'deactivate'}
        onOpenChange={(open) => !open && setPending(null)}
        title={`Desativar ${pending?.user.name ?? ''}?`}
        description="A pessoa perde o acesso agora: as sessões abertas são encerradas em todos os aparelhos. O histórico fica guardado e você pode reativar depois."
        confirmLabel="Desativar acesso"
        destructive
        busy={action.isPending}
        onConfirm={() => pending && action.mutate({ kind: 'deactivate', user: pending.user })}
      />
      <ConfirmDialog
        open={pending?.kind === 'cancel'}
        onOpenChange={(open) => !open && setPending(null)}
        title="Cancelar o convite?"
        description={`O link enviado para ${pending?.user.email ?? ''} deixa de valer e a pessoa sai da lista. Você pode convidá-la de novo depois.`}
        confirmLabel="Cancelar convite"
        destructive
        busy={action.isPending}
        onConfirm={() => pending && action.mutate({ kind: 'cancel', user: pending.user })}
      />
      <ConfirmDialog
        open={pending?.kind === 'reset2fa'}
        onOpenChange={(open) => !open && setPending(null)}
        title="Redefinir a verificação em duas etapas?"
        description={`Use quando ${pending?.user.name ?? 'a pessoa'} perdeu o celular ou os códigos. O app autenticador e os códigos de recuperação atuais deixam de valer e as sessões abertas são encerradas em todos os aparelhos; se o perfil exigir, a pessoa cadastra de novo no próximo login.`}
        confirmLabel="Redefinir"
        destructive
        busy={action.isPending}
        onConfirm={() => pending && action.mutate({ kind: 'reset2fa', user: pending.user })}
      />
    </section>
  );
}

function RowActions({
  user,
  isMe,
  actorIsAdmin,
  canCreate,
  canEdit,
  canDelete,
  disabled,
  onEdit,
  onResend,
  onCancel,
  onDeactivate,
  onReactivate,
  onResetTwoFactor,
}: {
  user: OrgUser;
  isMe: boolean;
  actorIsAdmin: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  disabled: boolean;
  onEdit: () => void;
  onResend: () => void;
  onCancel: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onResetTwoFactor: () => void;
}) {
  const invited = user.status === 'INVITED';
  if (user.role.key === 'FLUXUS_ADMIN' && !actorIsAdmin) return null;
  const items = [
    canEdit && { key: 'edit', label: 'Editar acesso', icon: Pencil, onSelect: onEdit },
    invited &&
      canCreate && {
        key: 'resend',
        label: canResendNow(user) ? 'Reenviar convite' : 'Reenviar convite (aguarde 1 min)',
        icon: Send,
        onSelect: onResend,
        disabled: !canResendNow(user),
      },
    invited &&
      canDelete && { key: 'cancel', label: 'Cancelar convite', icon: Ban, onSelect: onCancel, danger: true },
    user.status === 'ACTIVE' &&
      canEdit &&
      !isMe && {
        key: 'deactivate',
        label: 'Desativar acesso',
        icon: UserRoundX,
        onSelect: onDeactivate,
        danger: true,
      },
    user.status === 'DISABLED' &&
      canEdit && { key: 'reactivate', label: 'Reativar acesso', icon: RotateCcw, onSelect: onReactivate },
    user.twoFactorEnabled &&
      canEdit &&
      !isMe && {
        key: 'reset2fa',
        label: 'Redefinir verificação em duas etapas',
        icon: ShieldOff,
        onSelect: onResetTwoFactor,
        danger: true,
      },
  ].filter(Boolean) as {
    key: string;
    label: string;
    icon: LucideIcon;
    onSelect: () => void;
    disabled?: boolean;
    danger?: boolean;
  }[];
  if (!items.length) return null;
  const firstDanger = items.findIndex((i) => i.danger);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Ações para ${user.name}`} disabled={disabled}>
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((item, index) => (
          <Fragment key={item.key}>
            {index === firstDanger && index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              disabled={item.disabled}
              onSelect={item.onSelect}
              className={cn(item.danger && 'text-status-red-fg')}
            >
              <item.icon aria-hidden />
              {item.label}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
